import { useTranslation } from 'react-i18next';
import { Button, Loader, Modal, Span, TriangleAlertIcon } from '@uni-space/uni-components';
import { ParcelDto } from '@shared/types/parcel';
import { CancellationReasonItem, useCancellationReasons } from '../model/useCancellationReasons';

interface Props {
  parcel: ParcelDto;
  /** Причина, по которой сейчас идёт запрос отмены; null — ждём выбора. */
  pendingReason: CancellationReasonItem | null;
  errorMessage?: string;
  onSelect: (reason: CancellationReasonItem) => void;
  onClose: () => void;
}

/**
 * Выбор причины отмены по скану: нажатие на причину сразу отменяет посылку, после чего оператор
 * делает снимок вложения. Причин мало, поэтому они разложены кнопками — без выпадающего списка и
 * подтверждения, чтобы на конвейере это был один клик.
 */
export default function CancellationReasonModal({
  parcel,
  pendingReason,
  errorMessage,
  onSelect,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const reasons = useCancellationReasons();
  const isSending = pendingReason !== null;

  return (
    <Modal isOpen onClose={isSending ? () => {} : onClose}>
      <div className="cpc-reasonModal">
        <div>
          <div className="cpc-title">{t('cancellationPhoto.reasonModal.title')}</div>
          <div className="cpc-reasonModalTrack">
            <span className="cpc-code">{parcel.trackNumber}</span>
            {parcel.service?.name ? <span className="cpc-muted">{parcel.service.name}</span> : null}
          </div>
          <div className="cpc-hint">{t('cancellationPhoto.reasonModal.hint')}</div>
        </div>

        <div className="cpc-reasonList">
          {reasons.map((item) => (
            <Button
              key={item.id}
              type="error"
              width="100%"
              disabled={isSending}
              loading={pendingReason?.id === item.id}
              onClick={() => onSelect(item)}
            >
              {item.label}
            </Button>
          ))}
        </div>

        {errorMessage && (
          <div className="cpc-reasonError">
            <TriangleAlertIcon color="#EF6D7C" size="s" />
            <Span fs={12}>{errorMessage}</Span>
          </div>
        )}

        {isSending ? (
          <div className="cpc-reasonSending">
            <Loader size="s" />
            <Span fs={12}>{t('cancellationPhoto.reasonModal.sending')}</Span>
          </div>
        ) : (
          <Button width="100%" size="s" onClick={onClose}>
            {t('close')}
          </Button>
        )}
      </div>
    </Modal>
  );
}
